import { INVENTORY_ID_PATTERN } from './inventory.ts';
import type { InventoryStatus } from './status.ts';

/**
 * Shared helpers for the one-shot / ongoing CSV backfill. Used by BOTH the
 * in-app uploader (apps/admin/src/lib/csv.ts) and the CLI script
 * (scripts/import-csv.ts) so the two paths normalize identically.
 *
 * Background: the NCCHD source spreadsheet does not have a "Status" column.
 * Lifecycle status lives in the "Tag" column, which is overloaded — some
 * values are genuine status signals ("Sold", "Sold taken off website",
 * "Disable") while others are real display tags ("Summer Sale", "Call for
 * Quote"). deriveImportStatus() splits the two.
 */

/** Result of interpreting the overloaded "Tag" column. */
export type DerivedImportStatus = {
  status: InventoryStatus;
  /** Display tag to keep, or null when the tag was really a status signal. */
  tag: string | null;
};

/**
 * Map a raw "Tag" cell to a lifecycle status + the display tag to keep.
 *
 * Mapping (confirmed with NCCHD for the 2026 platform cutover):
 *   blank                          -> published, no tag
 *   "sold … taken off website"     -> hidden   (staff pulled it from the site)
 *   "sold" (any other phrasing)    -> sold      (shown publicly with a Sold badge)
 *   "disable"                      -> hidden
 *   anything else (promos, "call   -> published, tag kept as-is
 *     for pricing", etc.)
 */
export function deriveImportStatus(rawTag: string | null | undefined): DerivedImportStatus {
  const tag = (rawTag ?? '').trim();
  const low = tag.toLowerCase();
  if (!tag) return { status: 'published', tag: null };
  if (low.startsWith('disable')) return { status: 'hidden', tag: null };
  if (low.includes('sold')) {
    // "taken off website" / "off website" = explicitly removed from the site.
    if (low.includes('taken off') || low.includes('off website')) {
      return { status: 'hidden', tag: null };
    }
    return { status: 'sold', tag: null };
  }
  return { status: 'published', tag };
}

/**
 * Deterministically derive a schema-valid inventory id (`######-##`) from an
 * arbitrary seed. Deterministic on purpose: the import upserts on
 * inventory_id, so a stable generated id keeps re-runs idempotent (the same
 * bad row maps to the same id every time) instead of creating duplicates.
 */
export function generateInventoryId(seed: string, year?: number | null): string {
  let h = 0x811c9dc5; // FNV-1a 32-bit offset basis
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  const six = (h % 1_000_000).toString().padStart(6, '0');
  const yy = ((((year ?? 0) % 100) + 100) % 100).toString().padStart(2, '0');
  return `${six}-${yy}`;
}

/**
 * Resolve the final inventory id for an import row: keep the original when it
 * is already schema-valid and not a duplicate; otherwise mint a stable,
 * collision-free id from `seed`. `used` accumulates ids taken so far in this
 * import (mutated) and guarantees uniqueness — including catching two rows
 * that legitimately share the same valid id in the source sheet.
 */
export function resolveInventoryId(
  rawId: string | null | undefined,
  seed: string,
  year: number | null,
  used: Set<string>,
): { id: string; generated: boolean } {
  const original = (rawId ?? '').trim();
  if (INVENTORY_ID_PATTERN.test(original) && !used.has(original)) {
    used.add(original);
    return { id: original, generated: false };
  }
  let candidate = generateInventoryId(seed || original || 'row', year);
  let n = 0;
  while (used.has(candidate)) {
    n += 1;
    candidate = generateInventoryId(`${seed}#${n}`, year);
  }
  used.add(candidate);
  return { id: candidate, generated: true };
}
